import { assert } from "@/common/assert";

// type Promisify<T> = T extends Promise<infer TResult> ? T : Promise<T>;
export function wait(ms: number): Promise<void> {
    return new Promise((res) => {
        setTimeout(res, ms);
    });
}

export function asyncMapLimit<T, TResult>(
    src: T[],
    limit: number,
    mapper: (arg: T) => JQueryPromise<TResult>,
): JQueryPromise<TResult>[] {
    assert(limit > 0, "limit is not positive");
    const deferreds = src.map(() => $.Deferred<TResult>());
    let index = 0;
    let pendingCount = 0;
    function schedule() {
        while (index < src.length && pendingCount < limit) {
            const arg = src[index];
            pendingCount++;
            const result = mapper(arg);
            const deferred = deferreds[index];
            assert(deferred, "deferred is falsy??");
            result
                .done((d) => deferred.resolve(d))
                .fail((f) => deferred.reject(f))
                .always(() => {
                    pendingCount--;
                    schedule();
                });
            index++;
        }
    }
    schedule();
    return deferreds;
}

type PromiseOrderedFunc = <T>($src: JQueryPromise<T> | JQueryDeferred<T>, tag?: string) => JQueryPromise<T>;

export const promiseOrdered = (() => {
    const $doc = $(document);
    function _promiseOrdered(queueName = "_DQ_") {
        return <T>($src: JQueryPromise<T>, tag?: string): JQueryPromise<T> => {
            //see also https://api.jquery.com/queue/
            const $d = $.Deferred<T>();
            if (tag) console.log(`QUEUE: ${tag}`);
            $doc.queue(queueName, (next) => {
                $src.done((d) => $d.resolve(d))
                    .fail((f) => $d.reject(f))
                    .always(() => {
                        if (tag) {
                            console.log(`RESOLVED: ${tag}`);
                        }
                        next();
                    });
            });
            if ($doc.queue(queueName).length === 1) {
                //in case of callers repeatedly call promiseOrdered in synchronous manner, we should not dequeue immediately.
                setTimeout(() => {
                    $doc.dequeue(queueName);
                }, 0);
            }
            return $d.promise();
        };
    }
    const _defaultFunc = _promiseOrdered();

    function wrapper<T>($src: JQueryPromise<T> | JQueryDeferred<T>, tag?: string): JQueryPromise<T>;
    function wrapper(queueName: string): PromiseOrderedFunc;
    function wrapper<T>(firstArg: string | JQueryPromise<T>, tag?: string) {
        if (typeof firstArg === "string") {
            return _promiseOrdered(firstArg);
        }
        return _defaultFunc(firstArg, tag);
    }
    return wrapper;
})();

export function retriedWaitFor<T>(
    evaluator: () => T,
    tag: string,
    checkIntervalMs = 100,
    timeoutMs = 5000,
    additionalMessage = "",
): Promise<T> {
    return new Promise<T>((res, rej) => {
        let accumulatedMs = 0;
        const value = evaluator();
        if (value) {
            res(value);
        } else {
            const tid = setInterval(() => {
                accumulatedMs += checkIntervalMs;
                const valueAgain = evaluator();
                if (valueAgain) {
                    clearInterval(tid);
                    res(valueAgain);
                } else {
                    const timedout = accumulatedMs > timeoutMs;
                    if (timedout) {
                        clearInterval(tid);
                        rej(
                            new Error(
                                `wait for evaluator ${tag} timed out after ${timeoutMs} ms: ${additionalMessage}`,
                            ),
                        );
                    }
                }
            }, checkIntervalMs);
        }
    });
}

export async function retriedWaitForPromise<T>(
    evaluator: () => Promise<T>,
    checkIntervalMs = 100,
    timeoutMs = 5000,
): Promise<T> {
    const startTime = new Date().getTime();
    while (new Date().getTime() - startTime < timeoutMs) {
        const value = await evaluator();
        if (value) return value;
        await wait(checkIntervalMs);
    }
    throw new Error(`wait for evaluator promise timed out after ${timeoutMs} ms`);
}

export function fromJQueryPromise<T>($d: JQueryDeferred<T> | JQueryPromise<T>): Promise<T> {
    return new Promise<T>((res, rej) => {
        $d.done(res).fail(rej);
    });
}

export function toJQueryPromise<T>(p: Promise<T>): JQueryPromise<T> {
    const $d = $.Deferred<T>();
    p.then((d) => $d.resolve(d)).catch((e) => $d.reject(e));
    return $d;
}

//see https://css-tricks.com/debouncing-throttling-explained-examples/

type Promisify<T> = T extends JQueryPromise<infer TResult> ? T : JQueryPromise<T>;

// tslint:disable-next-line:no-any
type AnyFunc = (...a: any) => any;
/** A debounce function that supports debouncing both sync functions & async functions */
export type CancelledDueToDebouncingMessage = "cancelled due to debouncing";
export function debounce<T extends AnyFunc, TThis>(
    this: TThis,
    wait_ms: number,
    func: T,
): (...args: Parameters<T>) => Promisify<ReturnType<T>> {
    const that = this;
    let timerId: any;
    // tslint:disable-next-line: no-any
    let prevReject: ((reason: any) => void) | undefined;

    function isJQueryPromise<T extends AnyFunc>(p: ReturnType<T>): boolean {
        const result = p as JQueryPromise<unknown>;
        return !!(result && result.done && typeof result.done === "function");
    }

    function wrapper() {
        // eslint-disable-next-line prefer-rest-params
        const args = arguments;
        if (timerId) {
            if (prevReject) {
                const warnMessage: CancelledDueToDebouncingMessage = "cancelled due to debouncing";
                prevReject(warnMessage);
            }
            clearTimeout(timerId);
        }
        const $d = $.Deferred<T>();
        prevReject = (error: Error) => $d.reject(error);
        timerId = setTimeout(() => {
            try {
                // tslint:disable-next-line: no-any
                const result = func.apply(that, args as any) as ReturnType<T>;
                if (isJQueryPromise(result)) {
                    const pr = result as JQueryPromise<T>;
                    pr.done((d) => $d.resolve(d)).fail((f) => $d.reject(f));
                } else {
                    $d.resolve(result);
                }
            } catch (error) {
                $d.reject(error);
            }
        }, wait_ms);
        return $d.promise();
    }
    // tslint:disable-next-line: no-any
    return wrapper as any;
}
